import type { ColDef, ColGroupDef, GridOptions } from "ag-grid-enterprise";

export const INITIAL_GRID_OPTION_KEYS: ReadonlySet<string> = new Set([
	"aggFuncs",
	"allowShowChangeAfterFilter",
	"autoSizeStrategy",
	"blockLoadDebounceMillis",
	"cacheOverflowSize",
	"cacheQuickFilter",
	"chartThemeOverrides",
	"chartToolPanelsDef",
	"columnMenu",
	"components",
	"createChartContainer",
	"customChartThemes",
	"debounceVerticalScrollbar",
	"debug",
	"defaultColGroupDef",
	"detailRowAutoHeight",
	"detailRowHeight",
	"dragAndDropImageComponent",
	"enableBrowserTooltips",
	"enableCellExpressions",
	"enableCellSpan",
	"enableFilterHandlers",
	"enableGroupEdit",
	"enableRtl",
	"ensureDomOrder",
	"excelStyles",
	"filterHandlers",
	"getDataPath",
	"getLocaleText",
	"getRowId",
	"gridId",
	"groupLockGroupColumns",
	"icons",
	"infiniteInitialRowCount",
	"initialState",
	"keepDetailRows",
	"keepDetailRowsCount",
	"loadingCellRendererSelector",
	"loadingOverlayComponent",
	"localeText",
	"maxBlocksInCache",
	"maxConcurrentDatasourceRequests",
	"noRowsOverlayComponent",
	"overlayComponent",
	"paginateChildRows",
	"paginationPageSizeSelector",
	"pivotPanelShow",
	"pivotSuppressAutoColumn",
	"processUnpinnedColumns",
	"reactiveCustomComponents",
	"renderingMode",
	"rowDragText",
	"rowModelType",
	"scrollbarWidth",
	"serverSideInitialRowCount",
	"serverSideOnlyRefreshFilteredGroups",
	"serverSidePivotResultFieldSeparator",
	"skipHeaderOnAutoSize",
	"stopEditingWhenCellsLoseFocus",
	"suppressAnimationFrame",
	"suppressAutoSize",
	"suppressBrowserResizeObserver",
	"suppressColumnVirtualisation",
	"suppressExpandablePivotGroups",
	"suppressGroupRowsSticky",
	"suppressLoadingOverlay",
	"suppressMaxRenderedRowRestriction",
	"suppressOverlays",
	"suppressPreventDefaultOnMouseWheel",
	"suppressPropertyNamesCheck",
	"suppressRowTransform",
	"suppressRowVirtualisation",
	"suppressSetFilterByDefault",
	"suppressTouch",
	"tabIndex",
	"tooltipInteraction",
	"tooltipMouseTrack",
	"tooltipShowMode",
	"tooltipTrigger",
	"undoRedoCellEditing",
	"undoRedoCellEditingLimit",
	"valueCache",
	"valueCacheNeverExpires",
	"viewportRowModelBufferSize",
	"viewportRowModelPageSize"
]);

export const WRAPPER_OWNED_GRID_OPTION_KEYS: ReadonlySet<string> = new Set([
	"cellSelection",
	"doesExternalFilterPass",
	"enterNavigatesVerticallyAfterEdit",
	"getRowId",
	"isExternalFilterPresent",
	"localeText",
	"onBodyScroll",
	"onCellFocused",
	"onColumnMoved",
	"onColumnResized",
	"onColumnValueChanged",
	"onColumnVisible",
	"onFilterChanged",
	"onFirstDataRendered",
	"onGridReady",
	"onSelectionChanged",
	"onSortChanged",
	"onStateUpdated",
	"rowData",
	"rowSelection",
	"theme"
]);

export const NOT_AUTO_FORWARDABLE_GRID_OPTION_KEYS: ReadonlySet<string> = new Set(["columnDefs"]);

export interface GridOptionsDiff {
	creationTimeOnlyKeys: string[];
	notAutoForwardableKeys: string[];
	updates: Partial<GridOptions>;
}

const MAX_COMPARISON_DEPTH = 12;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (typeof value !== "object" || value === null) return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};

const haveEqualItems = (a: unknown[], b: unknown[], depth: number): boolean =>
	a.length === b.length && a.every((item, index) => areGridOptionValuesEqual(item, b[index], depth + 1));

const haveEqualEntries = (a: Record<string, unknown>, b: Record<string, unknown>, depth: number): boolean => {
	const keys = Object.keys(a);
	return (
		keys.length === Object.keys(b).length &&
		keys.every((key) => key in b && areGridOptionValuesEqual(a[key], b[key], depth + 1))
	);
};

export const areGridOptionValuesEqual = (a: unknown, b: unknown, depth = 0): boolean => {
	if (Object.is(a, b)) return true;
	if (depth >= MAX_COMPARISON_DEPTH) return false;
	if (Array.isArray(a) && Array.isArray(b)) return haveEqualItems(a, b, depth);
	if (isPlainObject(a) && isPlainObject(b)) return haveEqualEntries(a, b, depth);
	return false;
};

const columnIdentity = (def: ColDef & ColGroupDef): unknown => {
	if (!isPlainObject(def)) return def;
	const { children, colId, field, headerName, hide, pinned, rowGroup, type } = def;
	return { children: columnShape(children), colId, field, headerName, hide, pinned, rowGroup, type };
};

const columnShape = (colDefs: unknown): unknown => (Array.isArray(colDefs) ? colDefs.map(columnIdentity) : colDefs);

const comparableKeys = (previous: Record<string, unknown>, next: Record<string, unknown>): string[] =>
	[...new Set([...Object.keys(previous), ...Object.keys(next)])].filter(
		(key) => !WRAPPER_OWNED_GRID_OPTION_KEYS.has(key)
	);

const changedKeys = (previous: Record<string, unknown>, next: Record<string, unknown>): string[] =>
	comparableKeys(previous, next).filter((key) => !areGridOptionValuesEqual(previous[key], next[key]));

const shapeChangedKeys = (
	changed: string[],
	previous: Record<string, unknown>,
	next: Record<string, unknown>
): string[] =>
	changed
		.filter((key) => NOT_AUTO_FORWARDABLE_GRID_OPTION_KEYS.has(key))
		.filter((key) => !areGridOptionValuesEqual(columnShape(previous[key]), columnShape(next[key])));

export const diffGridOptions = (previous: GridOptions | undefined, next: GridOptions | undefined): GridOptionsDiff => {
	const previousOptions = (previous ?? {}) as Record<string, unknown>;
	const nextOptions = (next ?? {}) as Record<string, unknown>;
	const changed = changedKeys(previousOptions, nextOptions);
	const forwardable = changed.filter((key) => !NOT_AUTO_FORWARDABLE_GRID_OPTION_KEYS.has(key));
	return {
		creationTimeOnlyKeys: forwardable.filter((key) => INITIAL_GRID_OPTION_KEYS.has(key)),
		notAutoForwardableKeys: shapeChangedKeys(changed, previousOptions, nextOptions),
		updates: Object.fromEntries(
			forwardable.filter((key) => !INITIAL_GRID_OPTION_KEYS.has(key)).map((key) => [key, nextOptions[key]])
		) as Partial<GridOptions>
	};
};
